/**
 * Prepares a partner's custom CSS (css_overwrites) for adoption INSIDE the widget's shadow root.
 *
 * Inside a shadow tree the selectors partners have always anchored on the document never match:
 * `:root`, `html`, `body`, the custom element itself (`calenso-booking …`) and any wrapper of the
 * host page (`#booking .calenso-booking h1`) — the shadow tree starts below the host element. Leading
 * compounds that sit at or above the host are re-targeted to the shadow host, and — because a
 * partner's stylesheet has always been the LAST one in the cascade and must keep winning and losing
 * exactly the same fights against the widget's own rules — the rewrite preserves each selector's
 * SPECIFICITY, not just its meaning:
 *
 *   `:root { --main-color: … }`             → `:host { … }`                        (0,1,0) = (0,1,0)
 *   `:root .cbw-x { … }`                    → `:host .cbw-x { … }`                 (0,2,0) = (0,2,0)
 *   `html * { font-size: … }`               → `:where(:host) *:not(never) { … }`   (0,0,1) = (0,0,1)
 *   `body .calenso-booking div { … }`       → `:where(:host) .calenso-booking div:not(never)`   (0,1,2)
 *   `html body #calenso-booking .x { … }`   → `:where(:host) #calenso-booking .x:not(never):not(never)`
 *   `calenso-booking .form-control { … }`   → `:where(:host) .form-control:not(never)`          (0,1,1)
 *   `#booking .calenso-booking h1 { … }`    → `:where(:host) .calenso-booking h1:not(#never)`   (1,1,1)
 *                                             (only when the host really sits inside a `#booking`)
 *   `body { color: …; .calenso-booking { h1 { … } } }`
 *                                           → `:host { color: …; :where(&) .calenso-booking:not(never) { h1 { … } } }`
 *
 * `:where(:host)` contributes nothing, and `:not(never)` / `:not(.never)` / `:not(#never)` match every
 * element (nothing is a `<never>`, `.never` or `#never`) while contributing exactly the type / class /
 * id point the stripped compound used to contribute. `:root` is a pseudo-class, so `:host` (also a
 * pseudo-class) is already exact. A rule whose ONLY compound is document-level (`body { … }`) becomes
 * `:host { … }`: declared custom properties on the host then win the tie against the widget's own
 * `:host { --… }` defaults, which is what proximity gave them in the light DOM (a bare `html { … }`
 * lost that fight and stays weightless). Rules nested inside such a bare rule (CSS nesting) get
 * `:where(&)` instead of the implicit `&`, plus the same padding, so `body { .x { … } }` keeps
 * `(0,1,1)` instead of jumping to `(0,2,0)`.
 *
 * Page wrappers are recognised at runtime: `hostAncestorMatches(compound)` answers whether the host
 * or one of its ancestors matches the compound (`host.closest(compound)`). If it does, the light DOM
 * matched EVERY widget element through that ancestor, so dropping the compound (with its specificity
 * padded back) is exact; if it does not, the compound can only match inside the widget and is left
 * alone. Only descendant combinators are followed for these — `#booking > .x` never crossed the
 * custom element on the light DOM either.
 *
 * Everything else — `#calenso-booking …`, `.cbw-*`, bare classes, `@media`, `@font-face`,
 * `@import`, `svg:not(:root)`, non-leading tokens — is left untouched. The un-rewritten original is
 * still appended to the document (see `WidgetComponent.applyCustomCss()`), so rules that target
 * surfaces outside the widget — CDK overlays, the document body, `@font-face` — keep working
 * exactly as before. Comments are dropped from the shadow copy.
 */
export function rewriteCustomCssForShadowRoot(
  css: string,
  hostElementName: string,
  hostAncestorMatches: (compound: string) => boolean = () => false,
): string {
  const documentLevel = new Set([':root', 'html', 'body', hostElementName.toLowerCase()]);
  return new ShadowCssRewriter(documentLevel, hostAncestorMatches).rewrite(stripComments(css));
}

/** Selectors that never match, so `:not(…)` of them is true for every element and worth exactly one point of its tier. */
const NEVER = { ids: '#never', classes: '.never', types: 'never' };
type Specificity = { ids: number; classes: number; types: number };
const CONDITIONAL_AT_RULES = /^@(media|supports|layer|container|scope|document|-moz-document)\b/i;

interface RewriteContext {
  /** Set while emitting the block of a bare document-level rule (`body { … }` → `:host { … }`). */
  nestedPadding: string | null;
  /** Set while emitting the block of any style rule: nested preludes are relative to their parent, never to the page. */
  insideStyleRule: boolean;
  /** Block nesting depth — beyond MAX_DEPTH the block is copied verbatim instead of recursed into. */
  depth: number;
}

/** Real partner CSS nests a handful of levels; anything deeper is copied through untouched (bounded recursion, bounded time). */
const MAX_DEPTH = 32;

class ShadowCssRewriter {
  constructor(
    private readonly documentLevel: Set<string>,
    private readonly hostAncestorMatches: (compound: string) => boolean,
  ) {}

  rewrite(css: string): string {
    return this.rewriteBlockBody(css, { nestedPadding: null, insideStyleRule: false, depth: 0 });
  }

  /** Rewrites a run of declarations / rules (the inside of a block, or the top level). */
  private rewriteBlockBody(css: string, context: RewriteContext): string {
    if (context.depth > MAX_DEPTH) {
      return css;
    }
    let out = '';
    let i = 0;
    while (i < css.length) {
      const boundary = findStatementBoundary(css, i);
      if (boundary.index === -1) {
        out += css.slice(i);
        break;
      }
      const head = css.slice(i, boundary.index);
      if (boundary.char !== '{') {
        // declaration, `@import …;`, or a stray `}` — copied verbatim
        out += head + boundary.char;
        i = boundary.index + 1;
        continue;
      }
      const blockEnd = findMatchingBrace(css, boundary.index);
      const body = css.slice(boundary.index + 1, blockEnd);
      const prelude = head.trim();
      if (prelude.startsWith('@')) {
        out += head + '{' + (CONDITIONAL_AT_RULES.test(prelude) ? this.rewriteBlockBody(body, { ...context, depth: context.depth + 1 }) : body) + '}';
      } else {
        const rewritten = this.rewritePrelude(prelude, context);
        const leading = (/^\s*/.exec(head) as RegExpExecArray)[0];
        out += leading + rewritten.prelude + ' {' + this.rewriteBlockBody(body, { nestedPadding: rewritten.nestedPadding, insideStyleRule: true, depth: context.depth + 1 }) + '}';
      }
      i = blockEnd + 1;
    }
    return out;
  }

  private rewritePrelude(prelude: string, context: RewriteContext): { prelude: string; nestedPadding: string | null } {
    const selectors = splitTopLevel(prelude, ',').map(selector => selector.trim()).filter(Boolean);
    const results = selectors.map(selector => this.rewriteComplexSelector(selector, context));
    const nestedPaddings = results.map(result => result.nestedPadding).filter((padding): padding is string => padding !== null);
    return {
      prelude: results.map(result => result.selector).join(', '),
      // rules nested inside this one inherit the padding of the (first) bare document-level selector
      nestedPadding: nestedPaddings.length ? nestedPaddings[0] : null,
    };
  }

  private rewriteComplexSelector(selector: string, context: RewriteContext): { selector: string; nestedPadding: string | null } {
    const parts = tokenizeComplexSelector(selector);
    if (parts.length === 0) {
      return { selector, nestedPadding: null };   // a combinator-only prelude (`> { }`) — invalid CSS, left for the browser to drop
    }
    if (context.nestedPadding !== null) {
      // nested directly inside a bare `body { … }` (now `:host { … }`): `:where(&)` drops the host's
      // class point again and the padding restores the type point(s) the parent used to contribute
      const padded = parts.map((part, index) => index % 2 === 0 ? part.replace(/&/g, ':where(&)') : part);
      padded[padded.length - 1] = padCompound(padded[padded.length - 1], context.nestedPadding);
      const rebuilt = padded.join('');
      return { selector: selector.includes('&') ? rebuilt : ':where(&) ' + rebuilt, nestedPadding: null };
    }
    if (context.insideStyleRule) {
      return { selector, nestedPadding: null };   // relative to the parent rule, not to the page
    }
    const compounds = parts.filter((_, index) => index % 2 === 0);
    const combinatorAfter = (index: number) => parts[index * 2 + 1];
    let stripped = 0;
    let rootPoints = 0;
    const padding: Specificity = { ids: 0, classes: 0, types: 0 };
    while (stripped < compounds.length && this.documentLevel.has(compounds[stripped].toLowerCase())) {
      if (compounds[stripped].toLowerCase() === ':root') {
        rootPoints++;
      } else {
        padding.types++;
      }
      stripped++;
    }
    if (stripped === compounds.length) {
      if (stripped === 0) {
        return { selector, nestedPadding: null };
      }
      // The whole selector is document-level (`body`, `:root`, `html body`) → the host itself. A bare
      // `html { … }` stays weightless: on the light DOM it sat on the same element as the widget's own
      // `:root { --… }` defaults and lost to them, whereas `body`/`:root`/the element itself won.
      const onlyHtml = compounds.every(compound => compound.toLowerCase() === 'html');
      const nestedPadding = padding.types ? paddingFor({ ...padding, classes: padding.classes + rootPoints }) : null;
      return { selector: onlyHtml ? ':where(:host)' : ':host', nestedPadding };
    }
    // page wrappers above the host: strip while the compound matches an ancestor of the host and is
    // followed by a descendant combinator (a stripped compound must never be the subject)
    while (stripped < compounds.length - 1 && combinatorAfter(stripped) === ' ' && this.isHostAncestor(compounds[stripped])) {
      const specificity = compoundSpecificity(compounds[stripped]);
      padding.ids += specificity.ids;
      padding.classes += specificity.classes;
      padding.types += specificity.types;
      stripped++;
    }
    if (stripped === 0) {
      return { selector, nestedPadding: null };
    }
    // `:host` already carries one class point when a `:root` was stripped; the rest is padded onto the subject compound
    const rest = parts.slice(stripped * 2 - 1); // starts with the combinator that followed the last stripped compound
    rest[rest.length - 1] = padCompound(rest[rest.length - 1], paddingFor({ ...padding, classes: padding.classes + Math.max(0, rootPoints - 1) }));
    return { selector: (rootPoints > 0 ? ':host' : ':where(:host)') + rest.join(''), nestedPadding: null };
  }

  /** Does the host or one of its ancestors match this compound? Never asked for `&`, `:host…` or a lone pseudo-class. */
  private isHostAncestor(compound: string): boolean {
    if (compound === '&' || compound.startsWith(':host') || /^:[\w-]+$/.test(compound)) {
      return false;
    }
    try {
      return this.hostAncestorMatches(compound);
    } catch {
      return false;
    }
  }
}

function paddingFor(specificity: Specificity): string {
  return `:not(${NEVER.ids})`.repeat(specificity.ids) + `:not(${NEVER.classes})`.repeat(specificity.classes) + `:not(${NEVER.types})`.repeat(specificity.types);
}

/** Specificity of one compound selector — enough for the wrappers partners write (`#booking`, `div.page`, `main`). */
function compoundSpecificity(compound: string): Specificity {
  const specificity: Specificity = { ids: 0, classes: 0, types: 0 };
  // functional pseudo-classes: :where() is weightless, :not()/:is()/:has() count their most specific argument
  const rest = compound
    .replace(/:(not|is|has|where)\(((?:[^()]|\([^()]*\))*)\)/g, (_, name: string, args: string) => {
      if (name !== 'where') {
        const best = splitTopLevel(args, ',').map(argument => complexSpecificity(argument.trim()))
          .reduce((max, current) => compare(current, max) > 0 ? current : max, { ids: 0, classes: 0, types: 0 });
        specificity.ids += best.ids;
        specificity.classes += best.classes;
        specificity.types += best.types;
      }
      return '';
    })
    .replace(/\[[^\]]*\]/g, () => { specificity.classes++; return ''; })
    .replace(/::[\w-]+/g, () => { specificity.types++; return ''; });
  specificity.ids += (rest.match(/#[\w-]+/g) ?? []).length;
  specificity.classes += (rest.match(/\.[\w-]+/g) ?? []).length + (rest.match(/:[\w-]+/g) ?? []).length;
  specificity.types += /^-?[A-Za-z_][\w-]*/.test(rest) ? 1 : 0;
  return specificity;
}

function complexSpecificity(selector: string): Specificity {
  return tokenizeComplexSelector(selector).filter((_, index) => index % 2 === 0).map(compoundSpecificity)
    .reduce((sum, current) => ({ ids: sum.ids + current.ids, classes: sum.classes + current.classes, types: sum.types + current.types }), { ids: 0, classes: 0, types: 0 });
}

function compare(a: Specificity, b: Specificity): number {
  return a.ids - b.ids || a.classes - b.classes || a.types - b.types;
}

/** Appends the padding to the compound, ahead of any pseudo-element (`.x::before` → `.x:not(never)::before`). */
function padCompound(compound: string, padding: string): string {
  if (!padding) {
    return compound;
  }
  const pseudoElement = /::|:(before|after|first-line|first-letter)(?![\w-])/i.exec(compound);
  const at = pseudoElement ? pseudoElement.index : compound.length;
  return compound.slice(0, at) + padding + compound.slice(at);
}

/** Splits `a > b .c` into `['a', ' > ', 'b', ' ', '.c']` (compounds at even indexes, combinators at odd). */
function tokenizeComplexSelector(selector: string): string[] {
  const parts: string[] = [];
  let compound = '';
  let combinator = '';
  let depth = 0;
  let quote: string | null = null;
  for (let i = 0; i < selector.length; i++) {
    const char = selector[i];
    if (quote) {
      compound += char;
      if (char === '\\') {
        compound += selector[++i] ?? '';
      } else if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === '"' || char === '\'') {
      quote = char;
      compound += char;
      continue;
    }
    if (char === '(' || char === '[') {
      depth++;
    } else if (char === ')' || char === ']') {
      depth--;
    }
    if (depth === 0 && /[\s>+~]/.test(char)) {
      if (compound) {
        parts.push(compound);
        compound = '';
      }
      combinator += char;
      continue;
    }
    if (combinator) {
      parts.push(normalizeCombinator(combinator));
      combinator = '';
    }
    compound += char;
  }
  if (compound) {
    parts.push(compound);
  }
  return parts;
}

function normalizeCombinator(raw: string): string {
  const symbol = raw.replace(/\s+/g, '');
  return symbol ? ` ${symbol[0]} ` : ' ';
}

/** Splits on `separator` outside of parentheses, brackets and strings. */
function splitTopLevel(text: string, separator: string): string[] {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  let quote: string | null = null;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quote) {
      current += char;
      if (char === '\\') {
        current += text[++i] ?? '';
      } else if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === '"' || char === '\'') {
      quote = char;
    } else if (char === '(' || char === '[') {
      depth++;
    } else if (char === ')' || char === ']') {
      depth--;
    } else if (char === separator && depth === 0) {
      parts.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  parts.push(current);
  return parts;
}

/** Index and kind of the next top-level `{`, `;` or `}` at or after `from` (-1 if none). */
function findStatementBoundary(css: string, from: number): { index: number; char: string } {
  let depth = 0;
  let quote: string | null = null;
  for (let i = from; i < css.length; i++) {
    const char = css[i];
    if (quote) {
      if (char === '\\') {
        i++;
      } else if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === '"' || char === '\'') {
      quote = char;
    } else if (char === '(' || char === '[') {
      depth++;
    } else if (char === ')' || char === ']') {
      depth = Math.max(0, depth - 1);
    } else if (depth === 0 && (char === '{' || char === ';' || char === '}')) {
      return { index: i, char };
    }
  }
  return { index: -1, char: '' };
}

/** Index of the `}` closing the `{` at `open` (end of text if unbalanced). */
function findMatchingBrace(css: string, open: number): number {
  let level = 0;
  let quote: string | null = null;
  for (let i = open; i < css.length; i++) {
    const char = css[i];
    if (quote) {
      if (char === '\\') {
        i++;
      } else if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === '"' || char === '\'') {
      quote = char;
    } else if (char === '{') {
      level++;
    } else if (char === '}') {
      level--;
      if (level === 0) {
        return i;
      }
    }
  }
  return css.length;
}

function stripComments(css: string): string {
  return css.replace(/\/\*[\s\S]*?\*\//g, '');
}
